import { Modal, SubmitButton } from "@xola/ui-kit";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { PurchaseRuleLink } from "types";
import { api } from "@/api/api";
import { TermsTemplateTable } from "@/components/Agent/TermsTemplate/TermsTemplateTable";
import { NoTranslate } from "@/components/Common/NoTranslate";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { useInvalidate } from "@/hooks/useInvalidation";

interface ApplyTermsTemplateModalProps {
    isOpen: boolean;
    onClose: () => void;
    partners: ID[];
}

export const ApplyPartnerTermsTemplateModal = ({ isOpen, onClose, partners }: ApplyTermsTemplateModalProps) => {
    return (
        <Modal size="huge" isOpen={isOpen} onClose={onClose}>
            <ModalContent partners={partners} onClose={onClose} />
        </Modal>
    );
};

interface ModalContentProps {
    onClose: () => void;
    partners: ID[];
}

const ModalContent = ({ onClose, partners }: ModalContentProps) => {
    const [selectedTemplate, setSelectedTemplate] = useState<PurchaseRuleLink | null>(null);

    const { mutateAsync: updateTermsTemplate, isLoading, isSuccess } = useUpdateTermsTemplate();

    if (isSuccess) {
        onClose();
    }

    return (
        <>
            <Modal.Header>Commercial Terms Templates</Modal.Header>

            <Modal.Body className="!max-h-[70vh] overflow-auto">
                <TermsTemplateTable
                    showActions={false}
                    components={{
                        Name: ({ termsTemplate }) => (
                            <label className="space-x-2">
                                <input
                                    disabled={isLoading}
                                    checked={selectedTemplate?.id === termsTemplate.id}
                                    type="radio"
                                    onChange={() => setSelectedTemplate(termsTemplate)}
                                />

                                <NoTranslate>{termsTemplate.name ?? "-"}</NoTranslate>
                            </label>
                        ),
                    }}
                />
            </Modal.Body>

            <Modal.Footer>
                <SecondaryButton disabled={isLoading} onClick={onClose}>
                    Cancel
                </SecondaryButton>

                <SubmitButton
                    disabled={!selectedTemplate}
                    isLoading={isLoading}
                    onClick={async () => updateTermsTemplate({ partnerIds: partners, selectedTemplate })}
                >
                    Apply Template
                </SubmitButton>
            </Modal.Footer>
        </>
    );
};

interface useUpdateTermsTemplateProps {
    partnerIds: ID[];
    selectedTemplate: PurchaseRuleLink | null;
}

export const useUpdateTermsTemplate = () => {
    const invalidate = useInvalidate();

    return useMutation(async ({ partnerIds, selectedTemplate }: useUpdateTermsTemplateProps) => {
        for (const partnerId of partnerIds) {
            await api.patch(`/partners/${partnerId}`, { purchaseRuleLink: { id: selectedTemplate?.id } });
        }

        invalidate("purchaseRuleLinks", selectedTemplate?.id);
        invalidate("partners");
    });
};
